import axios, { AxiosError } from 'axios';

/**
 * Axios instance for the FastAPI backend.
 *
 * - In dev: same-origin (Vite proxies /api → http://localhost:8000)
 * - In prod: `VITE_API_URL` env at build time (e.g. https://api.10newtons.example)
 *
 * Auth: the access token is held in memory by the auth context and
 * attached via the request interceptor. Refresh tokens live in an
 * HttpOnly cookie — never read by JS.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  withCredentials: true,
});

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// SUPPORT-IMPERSONATION: while acting-as, an expired act-as access token
// must refresh from the *impersonation* cookie (/impersonation/refresh),
// NOT the superadmin's /auth cookie — otherwise a silent 401-refresh would
// pop the session back to the superadmin mid-impersonation. The
// ImpersonationContext flips this on enter and off on exit-to-root.
let impersonating = false;

export function setImpersonating(on: boolean): void {
  impersonating = on;
}

/** Current in-memory access token (null if logged out). Needed by the
 *  SSE live stream, whose EventSource/fetch must carry the token in the
 *  URL query because EventSource can't set an Authorization header. */
export function getAccessToken(): string | null {
  return accessToken;
}

// Called when a 401 can't be recovered (refresh cookie gone/expired) —
// the AuthProvider registers a handler that clears the user + shows the
// "session expired" notice and lets the route guard bounce to login.
let onSessionExpired: (() => void) | null = null;

export function registerSessionExpiredHandler(fn: (() => void) | null): void {
  onSessionExpired = fn;
}

// Transport-level failures (server unreachable: connection refused, DNS,
// timeout) arrive with no `error.response`. On staging the EC2 box is
// powered down outside business hours, so the portal surfaces a friendly
// off-hours notice instead of a raw error — see StagingOffHoursNotice.
// We also flag recovery on the next successful response so the notice
// clears the moment the box is back.
let onNetworkError: (() => void) | null = null;
let onNetworkRecovered: (() => void) | null = null;

export function registerNetworkHandlers(handlers: {
  onError?: (() => void) | null;
  onRecovered?: (() => void) | null;
}): void {
  onNetworkError = handlers.onError ?? null;
  onNetworkRecovered = handlers.onRecovered ?? null;
}

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Auth endpoints must never trigger the refresh-and-retry loop: a 401
// from /auth/login or /auth/refresh IS the terminal answer.
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/impersonation/refresh')
  );
}

// Single-flight refresh: many requests can 401 at once when the access
// token expires; they all await one /auth/refresh round-trip.
let refreshing: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    // Route to the impersonation refresh while acting-as, else the normal
    // superadmin/user refresh. Both re-mint an access token from their
    // respective HttpOnly cookie.
    const url = impersonating
      ? '/api/v1/impersonation/refresh'
      : '/api/v1/auth/refresh';
    refreshing = apiClient
      .post<{ access_token: string }>(url)
      .then((r) => {
        accessToken = r.data.access_token;
        return accessToken;
      })
      .catch(() => {
        accessToken = null;
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

// 401 → try one silent refresh (HttpOnly cookie) and replay the request.
// If the refresh fails, the session is truly over: clear state + notify.
apiClient.interceptors.response.use(
  (response) => {
    // A response of any status means the server is reachable again.
    onNetworkRecovered?.();
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as
      | (NonNullable<AxiosError['config']> & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;

    // No HTTP response (and not a deliberate cancel) → the server is
    // unreachable. Notify so the off-hours notice can decide whether to
    // surface (it only does on staging, outside business hours).
    if (!error.response && error.code !== 'ERR_CANCELED') {
      onNetworkError?.();
    }

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isAuthEndpoint(original.url)
    ) {
      original._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Replay: the request interceptor re-attaches the fresh token.
        return apiClient(original);
      }
      onSessionExpired?.();
    }

    return Promise.reject(error);
  },
);
